import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { buildConsolidated } from './consolidate'
import { MONO, fmt } from './format'
import { IncomeSubtotalRows } from './IncomeSubtotalRows'
import type { ProfitLossData } from '../types'

/**
 * Consolidated (€) Income by business unit › department, scoped to the same
 * fiscal YTD window as the net-profit summary above it. Built by
 * `buildConsolidated`: Spain (EUR) + US converted at each month's locked rate,
 * the same FX rule as the Income lines, so the unit rows add up to consolidated
 * Income. Income here is before the PC credit elimination, which is made once in
 * the group net (see ConsolidatedSummary) and is not allocated to a unit.
 */
export function ConsolidatedIncome({ report, ytdStart }: { report: ProfitLossData; ytdStart: number }) {
  const consolidated = useMemo(() => buildConsolidated(report), [report])
  const income = consolidated.sections.find((s) => s.id === 'income')
  if (!income) return null

  const sl = (a: number[]) => a.slice(ytdStart)
  const months = report.months.slice(ytdStart)
  const total = sl(income.total)
  const units = consolidated.unitSubtotals?.map((u) => ({ ...u, values: sl(u.values) }))
  const departments = consolidated.departmentSubtotals?.map((d) => ({ ...d, values: sl(d.values) }))
  const accountNames = Object.fromEntries(income.accounts.map((a) => [a.id, a.name]))
  const range = months.length ? `${months[0]} – ${months[months.length - 1]}` : ''
  const ytdLabel = `YTD '${String(report.ytdYear % 100).padStart(2, '0')}`
  const pcYtd = report.entityMeta.spain?.pcCreditMemoYtd
  const tableMinWidth = 300 + months.length * 82 + 120
  const dash = <span className="text-stone-300 dark:text-stone-600">—</span>

  return (
    <div data-testid="consolidated-income" className="rounded-2xl border border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900">
      <div className="px-5 pt-5">
        <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100">Income by business unit · {range}</h3>
        <p className="mt-0.5 text-xs text-stone-400 dark:text-stone-500">
          All figures in € · Spain + US converted at each month&apos;s average rate · before the PC credit elimination
          {pcYtd ? ` (${fmt(pcYtd, 'EUR')} YTD)` : ''}, which is made once in the group net above
        </p>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-sm" style={{ minWidth: tableMinWidth }}>
          <thead>
            <tr className="border-y border-stone-100 dark:border-stone-800">
              <th className="sticky left-0 z-20 bg-white px-4 py-2 text-left text-xs font-medium text-stone-500 dark:bg-stone-900 dark:text-stone-400">
                Account
              </th>
              {months.map((m) => (
                <th key={m} className="whitespace-nowrap px-2 py-2 text-right text-xs font-medium text-stone-500 dark:text-stone-400">
                  {m}
                </th>
              ))}
              <th className="whitespace-nowrap px-3 py-2 text-right text-xs font-semibold text-stone-600 dark:text-stone-300">
                {ytdLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr data-section="income" className="border-t border-stone-100 dark:border-stone-800/70">
              <th scope="row" className="sticky left-0 z-10 bg-white px-4 py-2 text-left text-sm font-medium text-stone-800 dark:bg-stone-900 dark:text-stone-100">
                {income.label}
              </th>
              {total.map((v, i) => (
                <td key={i} className={cn('whitespace-nowrap px-2 py-2 text-right text-xs font-semibold text-stone-700 dark:text-stone-200', MONO)}>
                  {v === 0 ? dash : fmt(v, 'EUR')}
                </td>
              ))}
              <td className={cn('whitespace-nowrap px-3 py-2 text-right text-xs font-semibold text-stone-800 dark:text-stone-100', MONO)}>
                {income.totalYtd === 0 ? dash : fmt(income.totalYtd, 'EUR')}
              </td>
            </tr>
            <IncomeSubtotalRows units={units} departments={departments} accountNames={accountNames} months={months} currency="EUR" />
          </tbody>
        </table>
      </div>
    </div>
  )
}
